import isEmpty from 'lodash/isEmpty';

import {
  axi,
  axiFootball,
  createBackendDateTimeString,
  getBackendAssetPath,
  getDateFromIsoString,
  GetListOutput,
  GetListParams,
} from '@shared/api';
import {SupportedLanguage, TranslationDict} from '@shared/types';
import {validateData} from '@shared/utils';
import {getCategories} from '../../category';
import {
  Event,
  EventAuditActor,
  EventDisplayType,
  EventLog,
  EventResult,
  EventStatus,
  ParticipantLogoOption,
} from '../types';
import {
  eventV8nSchema,
  v8nSchemaOfGetEventLogsResponse,
  v8nSchemaOfGetEventsResponse,
} from './backendSchemas';
import {
  EventOnBackend,
  GetEventLogsResponse,
  GetEventsResponse,
} from './backendTypes';
import {mapEventFromBackend} from './dataMappers';

export interface GetEventsParams extends GetListParams {
  statuses?: EventStatus[];
  searchQuery?: string;
  categoryId?: string;
  subCategoryId?: string;
  subCategoryIds?: string[];
  geoFilterOptionIds?: string[];
}

export const getEvents = async ({
  page,
  pageSize,
  statuses,
  searchQuery,
  categoryId,
  subCategoryId,
  subCategoryIds,
  geoFilterOptionIds,
}: GetEventsParams = {}): Promise<GetListOutput<Event>> => {
  const subCategoryIdsFinal = isEmpty(subCategoryIds)
    ? subCategoryId
      ? [subCategoryId]
      : undefined
    : subCategoryIds;

  const [response, categories] = await Promise.all([
    axi.get<GetEventsResponse>('/admin/events', {
      params: {
        page,
        per_page: pageSize,
        statuses: isEmpty(statuses) ? undefined : statuses,
        search_query: searchQuery,
        category: categoryId || undefined,
        subcategories: subCategoryIdsFinal,
        geo_filter: isEmpty(geoFilterOptionIds)
          ? undefined
          : geoFilterOptionIds,
      },
      paramsSerializer: {
        // no brackets at all for the following query string - ?page=1&per_page=9&type_slugs[]=INV&type_slugs[]=TBP
        indexes: null,
      },
    }),
    getCategories(),
  ]);

  validateData(response.data, v8nSchemaOfGetEventsResponse, 'getEvents');

  const {
    array,
    total,
    total_pages,
    page: pageFromBackend,
    per_page,
  } = response.data;
  const items = array?.map(item => mapEventFromBackend(item, categories)) ?? [];

  return {
    items,
    page: pageFromBackend,
    pageSize: per_page,
    total,
    totalPages: total_pages,
  };
};

const EVENT_SUPPORTED_LANGS: SupportedLanguage[] = [
  SupportedLanguage.English,
  SupportedLanguage.Portugal,
  SupportedLanguage.Tagalog,
  SupportedLanguage.Spanish,
];

const cleanEventTranslations = (
  trans: TranslationDict,
): Partial<TranslationDict> => {
  const result: Partial<TranslationDict> = {};
  for (const lang of EVENT_SUPPORTED_LANGS) {
    if (typeof trans[lang] === 'string' && trans[lang].trim()) {
      result[lang] = trans[lang];
    }
  }
  return result;
};

export interface CreateEventParams {
  nameTrans: TranslationDict;
  shortNameTrans: TranslationDict;
  descriptionTrans: TranslationDict;
  startDate: Date;
  endDate: Date;
  liveDate?: Date | null;
  yesPrice: number;
  noPrice: number;
  categoryId: string;
  subCategorySlug: string;
  topicId?: string | null;
  parentId: string | null;
  isPromotionNeeded: boolean;
  autoPublishEnabled: boolean;
  displayType: EventDisplayType;
  geoSlugs: string[];
}

export interface ConfirmEventsParams {
  eventSlugs: string[];
  confirm: boolean;
}

export interface UpdateEventParams {
  eventSlug: string;
  nameTrans: TranslationDict;
  descriptionTrans: TranslationDict;
  shortNameTrans: TranslationDict;
  isPromoted: boolean;
  subCategorySlugs: string[];
  startDate: Date;
  endDate: Date;
  liveDate?: Date | null;
  logoUrl: string;
  shortLogoUrl: string | null;
  yesPrice: number;
  noPrice: number;
  yesTextTrans: TranslationDict;
  noTextTrans: TranslationDict;
  topicSlug: string;
  overviewTrans?: TranslationDict;
  sourceOfTruthTrans?: TranslationDict;
  rulesTrans?: TranslationDict;
  displayType: EventDisplayType;
  participantLogoOption?: ParticipantLogoOption | null;
  participantShortLogoOption?: ParticipantLogoOption | null;
  autoPublishEnabled: boolean;
  geoSlugs: string[];
}

export const createEvent = ({
  nameTrans,
  shortNameTrans,
  descriptionTrans,
  startDate,
  endDate,
  liveDate,
  yesPrice,
  noPrice,
  categoryId,
  subCategorySlug,
  topicId,
  parentId,
  isPromotionNeeded,
  autoPublishEnabled,
  displayType,
  geoSlugs,
}: CreateEventParams): Promise<void> => {
  return axi.post('/admin/events', {
    name: cleanEventTranslations(nameTrans),
    short_name: cleanEventTranslations(shortNameTrans),
    description: cleanEventTranslations(descriptionTrans),
    start_datetime: createBackendDateTimeString(startDate),
    end_datetime: createBackendDateTimeString(endDate),
    start_date: createBackendDateTimeString(startDate),
    end_date: createBackendDateTimeString(endDate),
    ...(liveDate && {live_datetime: createBackendDateTimeString(liveDate)}),
    yes_cost: Math.round(yesPrice * 10_000),
    no_cost: Math.round(noPrice * 10_000),
    category: categoryId,
    subcategory_slug: subCategorySlug,
    topic_id: topicId || undefined,
    parent_id: parentId,
    need_promotion: isPromotionNeeded,
    auto_publish_enabled: autoPublishEnabled,
    display_type: displayType,
    geo_slugs: geoSlugs,
  });
};

export const confirmEvents = async ({
  eventSlugs,
  confirm,
}: ConfirmEventsParams): Promise<void> => {
  const payload = {
    event_slugs: eventSlugs,
    publish: confirm,
  };

  await axi.patch('/admin/events/publish', payload);
};

export type EventGroupType = 'VERTICAL' | 'HORIZONTAL';

export interface CreateEventGroupParams {
  eventSlugs: string[];
  groupType: EventGroupType;
}

export const createEventGroup = async ({
  eventSlugs,
  groupType,
}: CreateEventGroupParams): Promise<{id: string}> => {
  const {data} = await axi.post<{id: string}>('/admin/events/group', {
    event_slugs: eventSlugs,
    group_type: groupType,
  });

  return data;
};

export interface DeleteEventGroupParams {
  eventSlugs: string[];
}

export const deleteEventGroup = async ({
  eventSlugs,
}: DeleteEventGroupParams): Promise<void> => {
  await axi.delete('/admin/events/group', {
    data: {event_slugs: eventSlugs},
  });
};

export interface UpdateEventGroupParams {
  groupId: string;
  eventSlugs: string[];
}

export const updateEventGroup = async ({
  groupId,
  eventSlugs,
}: UpdateEventGroupParams): Promise<void> => {
  await axi.patch('/admin/events/group', {
    group_id: groupId,
    event_slugs: eventSlugs,
  });
};

export interface EventPrice {
  success: boolean;
  yesStartRatio: number;
}

export const getEventPrice = async (
  externalEventId: string,
): Promise<EventPrice> => {
  const {data} = await axiFootball.get<{
    success: boolean;
    yes_start_ratio: number;
  }>(`/admin/events/${externalEventId}/price`);

  return {success: data.success, yesStartRatio: data.yes_start_ratio};
};

export const updateEvent = async ({
  eventSlug,
  nameTrans,
  descriptionTrans,
  shortNameTrans,
  isPromoted,
  subCategorySlugs,
  startDate,
  endDate,
  liveDate,
  logoUrl,
  shortLogoUrl,
  yesPrice,
  yesTextTrans,
  noTextTrans,
  topicSlug,
  overviewTrans,
  sourceOfTruthTrans,
  rulesTrans,
  displayType,
  participantLogoOption,
  participantShortLogoOption,
  autoPublishEnabled,
  geoSlugs,
}: UpdateEventParams): Promise<void> => {
  const cleanTranslations = (
    translations: TranslationDict,
  ): Partial<TranslationDict> => {
    const cleanedTranslations: Partial<TranslationDict> = {};

    if (typeof translations[SupportedLanguage.English] === 'string') {
      cleanedTranslations[SupportedLanguage.English] =
        translations[SupportedLanguage.English];
    }

    (Object.entries(translations) as [SupportedLanguage, string][]).forEach(
      ([lang, trans]) => {
        if (lang === SupportedLanguage.English) {
          return;
        }

        if (trans.trim()) {
          cleanedTranslations[lang as keyof TranslationDict] = trans;
        }
      },
    );

    return cleanedTranslations;
  };

  const payload = {
    name: cleanTranslations(nameTrans),
    description: cleanTranslations(descriptionTrans),
    short_name: cleanTranslations(shortNameTrans),
    is_promoted: isPromoted,
    subcategories: subCategorySlugs,
    start_date: createBackendDateTimeString(startDate),
    end_date: createBackendDateTimeString(endDate),
    ...(liveDate && {live_date: createBackendDateTimeString(liveDate)}),
    logo_url: getBackendAssetPath(logoUrl),
    short_logo_url:
      shortLogoUrl === null ? null : getBackendAssetPath(shortLogoUrl),
    yes_price: Math.round(yesPrice * 10_000),
    no_price: 10_000 - Math.round(yesPrice * 10_000),
    yes_text: cleanTranslations(yesTextTrans),
    no_text: cleanTranslations(noTextTrans),
    topic_slug: topicSlug,
    tabs: {
      ...(overviewTrans && {overview: cleanTranslations(overviewTrans)}),
      ...(sourceOfTruthTrans && {
        source_of_truth: cleanTranslations(sourceOfTruthTrans),
      }),
      ...(rulesTrans && {rules: cleanTranslations(rulesTrans)}),
    },
    display_type: displayType,
    // Always send participant_logo_option/participant_short_logo_option so
    // the values are persisted on the backend.
    participant_logo_option: participantLogoOption ?? null,
    participant_short_logo_option: participantShortLogoOption ?? null,
    auto_publish_enabled: autoPublishEnabled,
    geo_slugs: geoSlugs,
  };

  await axi.put(`/admin/events/${eventSlug}`, payload);
};

export interface UpdateEventStatusParams {
  eventSlug: string;
  status: EventStatus;
  result: EventResult;
}

export const updateEventStatus = async ({
  eventSlug,
  status,
  result,
}: UpdateEventStatusParams): Promise<void> => {
  await axi.patch(`/admin/events/${eventSlug}/status`, {status, result});
};

export const getEvent = async (event_id: string): Promise<Event> => {
  const [response, categories] = await Promise.all([
    axi.get<EventOnBackend>(`/admin/events/${event_id}`),
    getCategories(),
  ]);

  validateData(response.data, eventV8nSchema, 'getEvent');

  return mapEventFromBackend(response.data, categories);
};

export interface GetEventLogsParams extends GetListParams {
  eventSlug: string;
}

export const getEventLogs = async ({
  eventSlug,
  page,
  pageSize,
}: GetEventLogsParams): Promise<GetListOutput<EventLog>> => {
  const response = await axi.get<GetEventLogsResponse>(
    `/admin/events/${eventSlug}/logs`,
    {
      params: {
        page,
        per_page: pageSize,
      },
    },
  );

  validateData(response.data, v8nSchemaOfGetEventLogsResponse, 'getEventLogs');

  const {
    array,
    page: pageFromBackend,
    per_page,
    total,
    total_pages,
  } = response.data;

  return {
    items: array.map(log => ({
      id: log.id,
      action: log.action,
      reason: log.reason,
      timestamp: getDateFromIsoString(log.timestamp),
      actorType: log.actor_type as EventAuditActor,
      actorId: log.actor_id,
      statusBefore: log.status_before as EventStatus | null,
      statusAfter: log.status_after as EventStatus | null,
      resultBefore: log.result_before as EventResult | null,
      resultAfter: log.result_after as EventResult | null,
    })),
    page: pageFromBackend,
    pageSize: per_page,
    total,
    totalPages: total_pages,
  };
};
